import { useEffect, useMemo, useState } from 'react';
import type { LiveRoom } from './api';
import { oneliveAssets } from './assets';
import { handleLiveRoomClick } from './liveEntryGuard';
import { useLiveUiMessages } from './useLiveUiMessages';

interface OneliveTopBannerProps {
  rooms: LiveRoom[];
}

const GRADIENT_HUES = [330, 260, 210, 170, 40, 10];

export default function OneliveTopBanner({ rooms }: OneliveTopBannerProps) {
  const slides = useMemo(() => {
    const featured = rooms.filter((r) => r.is_featured && r.status === 'live' && r.thumbnail);
    const source = featured.length > 0
      ? featured
      : rooms.filter((r) => r.status === 'live' && r.thumbnail);
    return source.slice(0, 6);
  }, [rooms]);

  const liveUiMessages = useLiveUiMessages();
  const [index, setIndex] = useState(0);

  useEffect(() => {
    setIndex(0);
  }, [slides.length]);

  useEffect(() => {
    if (slides.length <= 1) return undefined;
    const timer = window.setInterval(() => {
      setIndex((i) => (i + 1) % slides.length);
    }, 5000);
    return () => window.clearInterval(timer);
  }, [slides.length]);

  if (slides.length === 0) {
    return (
      <section className="onelive-topbanner onelive-topbanner-empty">
        <div className="onelive-topbanner-slide">
          <div className="onelive-topbanner-slide-bg" style={{ background: 'linear-gradient(135deg, #1a1a2e, #0f3460)' }} />
          <div className="onelive-topbanner-caption">
            <strong>원라이브TV</strong>
            <p>실시간 라이브 방송을 즐겨보세요</p>
          </div>
        </div>
      </section>
    );
  }

  const prev = () => setIndex((i) => (i - 1 + slides.length) % slides.length);
  const next = () => setIndex((i) => (i + 1) % slides.length);

  return (
    <section className="onelive-topbanner">
      <div className="onelive-topbanner-track" style={{ transform: `translateX(-${index * 100}%)` }}>
        {slides.map((room, i) => (
          <button
            key={room.uuid}
            type="button"
            className="onelive-topbanner-slide"
            onClick={() => handleLiveRoomClick(room, liveUiMessages, '/')}
          >
            {room.thumbnail ? (
              <img
                src={room.thumbnail}
                alt=""
                className="onelive-topbanner-image"
                loading={i === 0 ? 'eager' : 'lazy'}
                decoding="async"
              />
            ) : (
              <div
                className="onelive-topbanner-slide-bg"
                style={{
                  background: `linear-gradient(135deg, hsl(${GRADIENT_HUES[i % GRADIENT_HUES.length]} 65% 42%), hsl(${(GRADIENT_HUES[i % GRADIENT_HUES.length] + 50) % 360} 60% 28%))`,
                }}
              />
            )}
            <div className="onelive-topbanner-overlay" />
            <div className="onelive-topbanner-caption">
              <strong>{room.title}</strong>
              <p>{room.bj_nickname}</p>
            </div>
          </button>
        ))}
      </div>
      {slides.length > 1 && (
        <>
          <button type="button" className="onelive-topbanner-nav prev" onClick={prev} aria-label="이전">
            <img src={oneliveAssets.arrowLeftWhite} alt="" />
          </button>
          <button type="button" className="onelive-topbanner-nav next" onClick={next} aria-label="다음">
            <img src={oneliveAssets.arrowRightWhite} alt="" />
          </button>
          <div className="onelive-topbanner-dots">
            {slides.map((room, i) => (
              <button
                key={room.uuid}
                type="button"
                className={i === index ? 'active' : ''}
                onClick={() => setIndex(i)}
                aria-label={`슬라이드 ${i + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </section>
  );
}
